import { cloneDeep, find, isNull, remove } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PreferenceInput, ShareRequestInput, SummaryInput } from 'sr-types/lib/account/v1/graphql';
import { Preference } from 'sr-types/lib/search/v1/graphql';
import { updateFaroUser } from '../../faro/FaroClient';
import { client as accountClient } from '../auth/account';
import { FETCH_POLICY_CONFIG } from '../constants/constants';
import { usePrevious } from '../hooks/usePrevious';
import { AppDefinition } from '../nav/apps';
import { log } from '../utils/commonUtils';
import { deletePreferenceMutation, loadPreferencesQuery, savePreferenceMutation } from './Preferences';
import { OrganizationAccount, orgAccountDetails } from './account';
import { PermissionsType, UserProfile, fetchUserProfile, findPermissions, isTokenExpired } from './api';

export type UserContextType = {
    application: AppDefinition;
    setApplication: (AppDefinition) => void;
    isAuthenticated: boolean;
    setIsAuthenticated: (boolean) => void;
    userProfile: UserProfile;
    setUserProfile: (UserProfile) => void;
    activeOrganizationAccount: string;
    setActiveOrganizationAccount: (newOrgId: string, skipProfileFetch?: boolean) => Promise<boolean>;
    setOrganizationAccountDetails: (val) => void;
    organizationAccountDetails: OrganizationAccount;
    resetUserContext: () => void;
    preferencesLoaded: boolean;
    savePreference: (
        view: string,
        key: string,
        value: string,
        agent?: string | null,
        name?: string,
        summary?: SummaryInput,
        shareInput?: ShareRequestInput,
        accountId?: string,
        identity?: { id: string }
    ) => Promise<any>;
    deletePreference: (id: string) => Promise<any>;
    getPreference: (view: string, key: string, agent?: string) => any;
    getSharedPreferences: (view: string, agent?: string, key?: string) => Preference[];
    addOnLogoutListener: (listener: LogoutListener) => () => void;
    onLogout: () => void;
    preferences: Preference[];
    refreshPreferences: (ownerId?: string, updatedPreferences?: Preference[], forceRefresh?: boolean) => Promise<void>;
};

type LogoutListener = () => void;

export const UserContext = createContext<UserContextType>(undefined);

export const UserProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(!isTokenExpired());
    const prevIsAuthenticated = usePrevious(isAuthenticated);
    const [userProfile, setUserProfile] = useState<UserProfile>(undefined);
    const [activeOrganizationAccount, setActiveOrganizationAccount] = useState<string>(undefined);
    const [application, setApplication] = useState<AppDefinition>(undefined);
    const [organizationAccountDetails, setOrganizationAccountDetails] = useState<OrganizationAccount>();
    const [preferences, setPreferences] = useState<Preference[]>();
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);

    const logoutListeners = useRef<Set<LogoutListener>>(new Set());

    const addOnLogoutListener = useCallback((listener: LogoutListener) => {
        logoutListeners.current.add(listener);

        return () => {
            logoutListeners.current.delete(listener);
        };
    }, []);

    // Function to call all registered listeners during logout
    const onLogout = useCallback(() => {
        logoutListeners.current.forEach((listener) => listener());
    }, []);

    useEffect(() => {
        if (prevIsAuthenticated !== isAuthenticated) {
            log(isAuthenticated ? 'User is authenticated' : 'User is not authenticated');
        }
    }, [isAuthenticated, prevIsAuthenticated]);

    useEffect(() => {
        if (userProfile && activeOrganizationAccount) {
            updateFaroUser({
                email: userProfile.username,
                accountId: userProfile.accountId,
                orgId: activeOrganizationAccount
            });
        }
    }, [userProfile, activeOrganizationAccount]);

    /*
     * If we have logged in user we need to fetch org account details.
     */
    useEffect(() => {
        if (isAuthenticated && userProfile && activeOrganizationAccount) {
            // Skip this call if it's personal account.
            const currentOrg = find(userProfile.memberships, { id: activeOrganizationAccount });
            const isPersonal = currentOrg && currentOrg.personal;
            setOrganizationAccountDetails(undefined);
            if (!isPersonal) {
                accountClient
                    .query({
                        query: orgAccountDetails,
                        variables: { id: activeOrganizationAccount, organizationId: '' },
                        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                        context: {
                            headers: {
                                ownerId: activeOrganizationAccount
                            }
                        }
                    })
                    .then((res) => {
                        const aiSettings = res.data.organizationAccount?.aiSettings;
                        const updatedAIEnabledValue = isNull(aiSettings) ? true : aiSettings?.aiEnabled;
                        setOrganizationAccountDetails({
                            ...res.data.organizationAccount,
                            aiSettings: {
                                aiEnabled: updatedAIEnabledValue
                            }
                        });
                    })
                    .catch((err) => {
                        console.error('Error getting org account details:', err);
                        setOrganizationAccountDetails(undefined);
                    });
            }
        }
    }, [activeOrganizationAccount, isAuthenticated, userProfile]);

    useEffect(() => {
        if (userProfile) {
            //@ts-ignore
            window.pendo.identify({
                visitor: {
                    id: userProfile.accountId,
                    email: userProfile.username,
                    full_name: userProfile.name.firstName + ' ' + userProfile.name.lastName
                },
                account: {
                    id: userProfile.accountId
                }
            });
        } else {
            // Initialize Pendo for anonymous users on login/registration pages
            //@ts-ignore
            window.pendo.initialize({
                visitor: {
                    id: null // Null for anonymous users
                },
                account: {
                    id: 'ANONYMOUS'
                }
            });
        }
    }, [userProfile]);

    useEffect(() => {
        if (userProfile) {
            const currentOrg = activeOrganizationAccount
                ? find(userProfile.memberships, { id: activeOrganizationAccount })
                : null;
            const isPersonal = currentOrg && currentOrg.personal;
            const ownerId = isPersonal ? userProfile.accountId : activeOrganizationAccount;

            loadPreferencesQuery(ownerId).then((res) => {
                if (res?.data?.results?.hits?.items) {
                    setPreferences(res.data.results.hits.items);
                    setPreferencesLoaded(true);
                }
            });
        }
    }, [userProfile, activeOrganizationAccount]);

    useEffect(() => {
        window.onstorage = (event) => {
            if (event.key === 'auth-token' && !event.newValue) {
                setIsAuthenticated(false);
            }
        };
    }, []);

    const providerValue = useMemo(() => {
        const resetUserContext = () => {
            setIsAuthenticated(false);
            setUserProfile(undefined);
            setOrganizationAccountDetails(undefined);
            setActiveOrganizationAccount(undefined);
        };

        const savePreference = (
            view: string,
            key: string,
            value: string,
            agent: string = '',
            name?: string,
            summary?: SummaryInput,
            shareInput?: ShareRequestInput,
            accountId?: string,
            identity?: { id: string }
        ) => {
            const preferencePayload: PreferenceInput = {
                ...(identity ? { identity } : {}),
                ...(accountId ? { accountId } : {}),
                application: application.name,
                view,
                key,
                value,
                agent,
                name,
                summary
            };
            return savePreferenceMutation(userProfile.accountId, preferencePayload, shareInput).then(() => {
                setPreferences((prev) => {
                    const newPrefs = cloneDeep(prev);
                    remove(newPrefs, (p) => {
                        return (
                            p.accountId === userProfile.accountId &&
                            p.view === view &&
                            p.key === key &&
                            p.agent === agent &&
                            p.application === application.name
                        );
                    });
                    newPrefs.push({
                        accountId: userProfile.accountId,
                        application: application.name,
                        view,
                        key,
                        value,
                        agent,
                        name,
                        summary,
                        headline: '',
                        id: '',
                        uri: '',
                        shared: false
                    });
                    return newPrefs;
                });
            });
        };

        const deletePreference = (id: string) => {
            return deletePreferenceMutation(userProfile.accountId, id);
        };

        const getPreference = (view: string, key: string, agent: string = ''): any => {
            const pref: Preference = find(preferences, {
                accountId: userProfile.accountId,
                application: application.name,
                view: view,
                key: key,
                agent: agent
            });
            if (pref) {
                try {
                    const prefVal = JSON.parse(pref.value);
                    return prefVal;
                } catch (e) {
                    return pref.value; // Return as string if parsing fails
                }
            }
        };

        const getSharedPreferences = (view: string, agent: string = '', key?: string): Preference[] => {
            try {
                // Don't restrict by accountId so shared preferences (saved by other users) are also visible
                const source = Array.isArray(preferences) ? preferences : [];
                const matches = key
                    ? source.filter((p) => p.key === key && p.application === application.name)
                    : source.filter((p) => p.view === view && p.agent === agent && p.application === application.name);

                const results: any[] = [];
                matches.forEach((p) => {
                    const entry: any = { ...p };
                    if (typeof p?.value === 'string') {
                        try {
                            entry.value = JSON.parse(p.value);
                        } catch (e) {
                            entry.value = p.value;
                        }
                    }
                    results.push(entry);
                });
                return results;
            } catch {
                return [];
            }
        };

        const refreshPreferences = async (
            _ownerId?: string,
            updatedPreferences?: Preference[],
            forceRefresh = true
        ) => {
            if (!userProfile) return;

            try {
                if (updatedPreferences) setPreferences(updatedPreferences);
                if (forceRefresh) {
                    const res = await loadPreferencesQuery(activeOrganizationAccount);
                    const prefs = res?.data?.results?.hits?.items ?? [];
                    setPreferences(prefs);
                }
                setPreferencesLoaded(true);
            } catch (err) {
                console.warn('Failed to refresh preferences', err);
            }
        };

        return {
            application,
            setApplication,
            isAuthenticated,
            setIsAuthenticated,
            userProfile,
            setUserProfile,
            activeOrganizationAccount,
            setActiveOrganizationAccount: (newOrgId: string, skipProfileFetch: boolean = false) => {
                return new Promise<boolean>((resolve) => {
                    log(
                        'activeOrganizationAccount changing from:',
                        activeOrganizationAccount,
                        'to:',
                        newOrgId,
                        'loading user profile...'
                    );
                    /**
                     * We make fetchUserProfile call at two places. Other one is in AppRouter
                     * @param {string} newOrgId - The new org id to set
                     * @param {boolean} [skipProfileFetch=false] - Set to true if you don't want to fetch user profile
                     * @returns {Promise<boolean>}
                     */
                    if (skipProfileFetch) {
                        setActiveOrganizationAccount(newOrgId);
                        resolve(true);
                    } else {
                        fetchUserProfile(newOrgId, (data: UserProfile) => {
                            setUserProfile(data);
                            setActiveOrganizationAccount(newOrgId);
                            resolve(true);
                        });
                    }
                });
            },
            organizationAccountDetails,
            setOrganizationAccountDetails,
            resetUserContext,
            preferencesLoaded,
            savePreference,
            deletePreference,
            getPreference,
            getSharedPreferences,
            onLogout,
            addOnLogoutListener,
            preferences,
            refreshPreferences
        };
    }, [
        activeOrganizationAccount,
        application,
        isAuthenticated,
        organizationAccountDetails,
        preferences,
        preferencesLoaded,
        userProfile
    ]);

    return <UserContext.Provider value={providerValue}>{children}</UserContext.Provider>;
};

export const useFeaturePermissions = (feature: string): PermissionsType => {
    const { userProfile, application, activeOrganizationAccount } = useContext(UserContext);

    let ret: {};
    if (feature && activeOrganizationAccount && userProfile && userProfile.memberships) {
        const features = find(userProfile.memberships, { id: activeOrganizationAccount });
        if (features?.access) {
            const access = application.featureApps
                .map((app) => features.access.find((a) => a.application === app && a.feature === feature))
                .find(Boolean);
            if (access) {
                ret = findPermissions(access.permissions);
            }
        }
    }
    return ret || {};
};
